import React, { useCallback, useEffect, useState } from 'react';
import Panel from 'components/Panel';
import Break from 'components/Break';
import { BlueBorderButton, BlueButton } from 'components/Button';
import Text from 'components/Text';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelRowTextForm,
  PanelButtonRow,
  StyledForm,
  FullPanel,
  PanelContent
} from 'components/PanelComponents';
import SectionHeader from 'components/Panels/SectionHeader';
import {
  RESOURCE_TYPES,
  ERROR_MESSAGES,
  LOCAL_RESOURCE_MAPPED_VALUES
} from 'utils/appConstants';
import { revertSidePanel } from 'reducers/sidePanel/actions';
import { editDevice } from 'reducers/device/actions';
import { filterForAllowedProperties, isExternallyManaged } from 'utils/format';
import { EDIT_PROPERTIES } from 'api/device';
import { useDispatch, useSelector } from 'react-redux';
import Table from 'components/Table';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const deviceColumnDefs = () => {
  const columns = [
    {
      headerName: 'ID',
      field: 'deviceId'
    },
    {
      headerName: 'SFDI',
      field: 'sfdi'
    },
    {
      headerName: 'LFDI',
      field: 'lfdi'
    }
  ];
  if (externallyManagedEdevs) {
    columns.push({
      headerName: 'Remotely Managed',
      field: 'localDevice',
      cellRenderer: ({ value }) => isExternallyManaged(value)
    });
  }
  return columns;
};

const AddChildrenDevice = ({ isActive }) => {
  const dispatch = useDispatch();
  const devices = useSelector(state => state.device.devices);
  const parentTopoNodeId = useSelector(
    state => state.sidePanel.data.parentTopoNodeId
  );
  const parentAggregatorId = useSelector(
    state => state.sidePanel.data.parentAggregatorId
  );
  const parentIndeNodeId = useSelector(
    state => state.sidePanel.data.parentIndeNodeId
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchInputForAvailableDevices, setSearchInputForAvailableDevices] =
    useState('');
  const [
    searchInputForUnavailableDevices,
    setSearchInputForUnavailableDevices
  ] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    if (name === 'searchInputForAvailableDevices') {
      setSearchInputForAvailableDevices(value);
    } else if (name === 'searchInputForUnavailableDevices') {
      setSearchInputForUnavailableDevices(value);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const { WRONG_ENTITY_TYPE } = ERROR_MESSAGES;

    const editedDevice = filterForAllowedProperties(
      selectedDevice,
      EDIT_PROPERTIES
    );
    try {
      if (parentAggregatorId !== undefined) {
        editedDevice.aggregatorId = parentAggregatorId;
      } else if (parentIndeNodeId !== undefined) {
        const indeNodeIdList = editedDevice.indeNodeIdList || [];
        editedDevice.indeNodeIdList = [...indeNodeIdList, parentIndeNodeId];
      } else if (parentTopoNodeId !== undefined) {
        const topoNodeIdList = editedDevice.topoNodeIdList || [];
        editedDevice.topoNodeIdList = [...topoNodeIdList, parentTopoNodeId];
      } else {
        throw new Error(WRONG_ENTITY_TYPE);
      }
      if (externallyManagedEdevs) {
        // If it's local or remote_plus_local, it can remain that status
        // If it's local and being added to a local resource it can become remote_plus_local
        if (editedDevice.localDevice === LOCAL_RESOURCE_MAPPED_VALUES.REMOTE) {
          editedDevice.localDevice =
            LOCAL_RESOURCE_MAPPED_VALUES.REMOTE_PLUS_LOCAL;
        }
      }
      await dispatch(editDevice(editedDevice, selectedDevice.deviceId));
      dispatch(revertSidePanel({ data: editedDevice.deviceId }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSelectDevice = useCallback(row => {
    const selectedDevice = row.data;
    setSelectedDevice(selectedDevice);
  }, []);

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    const { WRONG_ENTITY_TYPE } = ERROR_MESSAGES;
    let entity = '';
    let id = '';
    let currentChildDeviceIds = [];
    if (parentAggregatorId !== undefined) {
      id = parentAggregatorId;
      entity = RESOURCE_TYPES.AGGREGATOR;
      currentChildDeviceIds = devices.reduce((acc, device) => {
        if (device.aggregatorId === id) {
          acc.push(device.deviceId);
        }
        return acc;
      }, []);
    } else if (parentIndeNodeId !== undefined) {
      id = parentIndeNodeId;
      entity = RESOURCE_TYPES.INDENODE;
      currentChildDeviceIds = devices.reduce((acc, device) => {
        if (device.indeNodeIdList?.includes(id)) {
          acc.push(device.deviceId);
        }
        return acc;
      }, []);
    } else if (parentTopoNodeId !== undefined) {
      id = parentTopoNodeId;
      entity = RESOURCE_TYPES.TOPONODE;
      currentChildDeviceIds = devices.reduce((acc, device) => {
        if (device.topoNodeIdList?.includes(id)) {
          acc.push(device.deviceId);
        }
        return acc;
      }, []);
    } else {
      throw new Error(WRONG_ENTITY_TYPE);
    }
    const availableDevices = [];

    /* Removing unavailable devices for now
    const unavailableDevices = [];
    */

    devices.forEach(device => {
      const { deviceId, sfdi, lfdi, topoNodeIdList } = device;

      // While Unavailable Devices is removed, we are not showing any devices for toponodes
      // that already have toponode associations
      const isNotTopoNode = entity !== RESOURCE_TYPES.TOPONODE;
      const canAddTopoNodes =
        entity === RESOURCE_TYPES.TOPONODE &&
        (!topoNodeIdList || topoNodeIdList.length === 0);

      const includesCaseInsensitiveAvailableInput =
        (isNotTopoNode || canAddTopoNodes) &&
        (deviceId
          .toUpperCase()
          .includes(searchInputForAvailableDevices.toUpperCase()) ||
          sfdi.toString().includes(searchInputForAvailableDevices) ||
          lfdi.toString().includes(searchInputForAvailableDevices));

      /* Removing unavailable devices for now
      const includesCaseInsensitiveUnavailableInput =
        deviceId
          .toUpperCase()
          .includes(searchInputForUnavailableDevices.toUpperCase()) ||
        sfdi.toString().includes(searchInputForUnavailableDevices) ||
        lfdi.toString().includes(searchInputForUnavailableDevices);
      */
      if (
        !currentChildDeviceIds.includes(device.deviceId) &&
        includesCaseInsensitiveAvailableInput
      ) {
        availableDevices.push(device);
      }
      /* Removing unavailable devices for now
      else if (
        currentChildDeviceIds.includes(device.deviceId) &&
        includesCaseInsensitiveUnavailableInput
      ) {
        unavailableDevices.push(device);
      }
      */
    });
    const parentType =
      entity === RESOURCE_TYPES.AGGREGATOR ? 'Aggregator' : 'Node';

    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel
            title={`Selected ${parentType} » Add Existing Device`}
            primaryHeader
          />
          <PanelContent>
            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>
                  <Text fontSize="12px">{`Parent ${parentType} ID`}</Text>
                </PanelRowDescriptor>
                <PanelRowDisplay>
                  <Text fontSize="12px">{id}</Text>
                </PanelRowDisplay>
              </SubPanelRow>
            </SubPanel>
            <Panel title="Available Devices">
              <SubPanel>
                <SectionHeader
                  header="Find Device by ID, SFDI, LFDI"
                  marginBottom="8px"
                />
                <PanelRowTextForm
                  name="searchInputForAvailableDevices"
                  onChange={handleInputChange}
                  value={searchInputForAvailableDevices}
                  autocomplete="off"
                  placeholder="Search..."
                />

                <Table
                  rowData={availableDevices}
                  columnDefs={deviceColumnDefs()}
                  height="300px"
                  width="100%"
                  padding="0px"
                  margin="10px 0px"
                  onRowClicked={onSelectDevice}
                />
              </SubPanel>
            </Panel>

            {/* Removing unavailable devices for now

            <Panel title="Unavailable Devices" collapsed={true}>
              <SubPanel>
                <Break margin="0 0 12px" />

                <SectionHeader
                  header="Find Device by ID, SFDI, LFDI"
                  marginBottom="8px"
                />
                <PanelRowTextForm
                  name="searchInputForUnavailableDevices"
                  onChange={handleInputChange}
                  value={searchInputForUnavailableDevices}
                  autocomplete="off"
                  placeholder="Search..."
                />

                <Table
                  className="unavailable-selection"
                  rowData={unavailableDevices}
                  columnDefs={deviceColumnDefs()}
                  height="300px"
                  width="100%"
                  padding="0px"
                  margin="10px 0px"
                />
              </SubPanel>
            </Panel> */}
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() => dispatch(revertSidePanel())}
            >
              Cancel
            </BlueBorderButton>
            <BlueButton
              type="submit"
              disabled={isSubmitting || !selectedDevice}
            >
              {`Attach to Selected ${parentType}`}
            </BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default AddChildrenDevice;
